<template>
  <div
    role="button"
    tabindex="0"
    class="unidentified-row flex w-full overflow-hidden"
    :class="
      compact
        ? 'unidentified-row--compact h-6'
        : autoHeight
          ? 'min-h-[34px]'
          : 'h-[72px] md:h-[105px]'
    "
    :title="tooltip"
    data-testid="unidentified-coverage-row"
    @click="emit('click')"
    @keydown.enter="emit('click')"
  >
    <div
      class="unidentified-row__bar"
      :class="compact ? 'w-1.5' : autoHeight ? 'w-2' : 'w-2 md:w-3'"
    />
    <div
      v-if="compact"
      class="flex items-center px-1 min-w-0 text-[10.5px] font-bold text-[#34344F]"
    >
      <span class="truncate">Ocupado</span>
    </div>
    <div
      v-else
      class="flex flex-col w-full min-w-0 px-1.5 py-1"
      :class="[autoHeight ? '' : 'md:px-2 md:py-1.5', 'text-left']"
    >
      <div class="flex items-center gap-1 min-w-0">
        <span class="unidentified-row__avatar" aria-hidden="true">
          <svg viewBox="0 0 24 24" class="w-2.5 h-2.5" :class="autoHeight ? '' : 'md:w-3 md:h-3'">
            <path :d="mdiAccountOutline" fill="currentColor" />
          </svg>
        </span>
        <span
          class="font-bold text-[#34344F]"
          :class="[
            autoHeight ? 'break-words' : 'truncate',
            mobile || autoHeight ? 'text-[11px]' : 'text-[9px] md:text-[11px]'
          ]"
          >Ocupado</span
        >
      </div>
      <span
        class="text-[#374151] pl-[18px]"
        :class="[
          autoHeight ? '' : 'md:pl-[22px]',
          mobile || autoHeight ? 'text-[10px]' : 'text-[8px] md:text-[9.5px]'
        ]"
      >
        s/ identificação
      </span>
      <span
        v-if="responsibleName"
        class="unidentified-row__tag"
        :class="{
          'unidentified-row__tag--mobile': mobile || autoHeight,
          'unidentified-row__tag--wrap': autoHeight
        }"
        data-testid="unidentified-coverage-responsible-tag"
      >
        {{ responsibleName }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiAccountOutline } from '@mdi/js'
import {
  UNIDENTIFIED_COVERAGE_COLORS as COLORS,
  unidentifiedCoverageStripes,
  unidentifiedCoverageTooltipParts
} from './unidentifiedCoverageStyles'

const props = withDefaults(
  defineProps<{
    responsibleName?: string | null
    note?: string | null
    compact?: boolean
    /** Grade do celular (R1b, `isMobileLayout`): texto ≥ 10px em vez do encolhimento do `max-md`. */
    mobile?: boolean
    /** Celular v2 (`cardLayout` ≠ default): altura pela do conteúdo e texto com quebra (spec v2 §8.3). */
    autoHeight?: boolean
  }>(),
  { responsibleName: null, note: null, compact: false, mobile: false, autoHeight: false }
)

const emit = defineEmits<{ click: [] }>()

const tooltip = computed(() =>
  unidentifiedCoverageTooltipParts(props.responsibleName, props.note).join(' · ')
)

// Consumidos via v-bind() no <style> (fonte única das cores em unidentifiedCoverageStyles.ts)
const stripes = unidentifiedCoverageStripes(7)
const stripesCompact = unidentifiedCoverageStripes(5)
const accent = COLORS.accent
const border = COLORS.border
const text = COLORS.text
</script>

<style scoped>
.unidentified-row {
  background: v-bind(stripes);
}
.unidentified-row--compact {
  background: v-bind(stripesCompact);
}
.unidentified-row__bar {
  flex-shrink: 0;
  background: v-bind(accent);
}
.unidentified-row__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border-radius: 9999px;
  border: 1.5px dashed v-bind(accent);
  background: #fff;
  color: v-bind(accent);
}
.unidentified-row__tag {
  margin-top: auto;
  align-self: flex-start;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: #fff;
  border: 1px solid v-bind(border);
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 9.5px;
  color: v-bind(text);
}
.unidentified-row__tag--mobile {
  font-size: 10px;
}
.unidentified-row__tag--wrap {
  white-space: normal;
  overflow-wrap: anywhere;
}
</style>
